import { useState, useContext, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";

import styles from "./LoginPage.module.css";
import usePageTitle from "../../hooks/usePageTitle";
import { TradeLyftLockup, BrandLoader } from "../../components/Brand";
import { sendForgotPasswordOTP, verifyForgotPasswordOTP, resetPassword } from "../../api/auth";
import OTPInput from "../../components/OTPInput/OTPInput";

const REMEMBER_USERNAME_KEY = "remembered_username";

const CHEVRON_DURATION = 2;

const CHEVRON_STROKE = {
    stroke: "var(--text-on-accent)",
    strokeWidth: 12.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    fill: "none",
};

const CHEVRON_MOTION = [
    {
        restOpacity: 1,
        initial: { opacity: 0.05, x: 0, y: 20 },
        animate: {
            opacity: [0.05, 0.05, 0.6, 1, 1, 0.05],
            y: [20, 20, -30, -30, 20],
        },
        transition: {
            opacity: {
                duration: CHEVRON_DURATION,
                times: [0, 0.2, 0.4, 0.6, 0.9, 1],
                ease: ["linear", "easeOut", "easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
            y: {
                duration: CHEVRON_DURATION,
                times: [0, 0.2, 0.6, 0.9, 1],
                ease: ["linear", "easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
        },
    },
    {
        restOpacity: 0.6,
        initial: { opacity: 0.05, x: 0, y: 20 },
        animate: {
            opacity: [0.05, 0.05, 0.45, 0.7, 0.7, 0.05],
            y: [20, 20, -30, -30, 20],
        },
        transition: {
            opacity: {
                duration: CHEVRON_DURATION,
                times: [0, 0.1, 0.3, 0.5, 0.85, 1],
                ease: ["linear", "easeOut", "easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
            y: {
                duration: CHEVRON_DURATION,
                times: [0, 0.1, 0.5, 0.85, 1],
                ease: ["linear", "easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
        },
    },
    {
        restOpacity: 0.3,
        initial: { opacity: 0.05, x: 0, y: 20 },
        animate: {
            opacity: [0.05, 0.3, 0.5, 0.5, 0.05],
            y: [20, -30, -30, 20],
        },
        transition: {
            opacity: {
                duration: CHEVRON_DURATION,
                times: [0, 0.25, 0.4, 0.8, 1],
                ease: ["easeOut", "easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
            y: {
                duration: CHEVRON_DURATION,
                times: [0, 0.4, 0.8, 1],
                ease: ["easeOut", "linear", "easeIn"],
                repeat: Infinity,
            },
        },
    },
];

const BRAND_CHEVRON_PATHS = [
    "M77.918 117.161L155.186 51.5511L232.454 117.161",
    "M77.918 187.458L155.186 121.848L232.454 187.458",
    "M77.918 257.755L155.186 192.145L232.454 257.755",
];

function renderChevrons(paths, motions, stroke, prefersReducedMotion) {
    return paths.map((d, index) => {
        const spec = motions[index];
        if (prefersReducedMotion) {
            return <path key={d} d={d} opacity={spec.restOpacity} {...stroke} />;
        }
        return (
            <motion.path
                key={d}
                d={d}
                {...stroke}
                initial={spec.initial}
                animate={spec.animate}
                transition={spec.transition}
            />
        );
    });
}

function BrandIllustration() {
    const prefersReducedMotion = useReducedMotion();

    return (
        <svg
            className={styles["brand-illustration-svg"]}
            width="310"
            height="300"
            viewBox="0 0 310 300"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M231.804 0H77.268C34.594 0 0 33.5712 0 74.9833V224.95C0 266.362 34.594 299.933 77.268 299.933H231.804C274.478 299.933 309.072 266.362 309.072 224.95V74.9833C309.072 33.5712 274.478 0 231.804 0Z"
                fill="var(--login-brand-bg)"
            />
            {renderChevrons(BRAND_CHEVRON_PATHS, CHEVRON_MOTION, CHEVRON_STROKE, prefersReducedMotion)}
        </svg>
    );
}

function TradeLyftLogo() {
    return <TradeLyftLockup size={64} showTm className={styles["brand-mark"]} />;
}

const MailIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
            d="M18.3337 5.00001C18.3337 4.08334 17.5837 3.33334 16.667 3.33334H3.33366C2.41699 3.33334 1.66699 4.08334 1.66699 5.00001M18.3337 5.00001V15C18.3337 15.9167 17.5837 16.6667 16.667 16.6667H3.33366C2.41699 16.6667 1.66699 15.9167 1.66699 15V5.00001M18.3337 5.00001L10.0003 10.8333L1.66699 5.00001"
            stroke="#667085"
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const EyeOffIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M9.9 4.24C10.5883 4.07888 11.2931 3.99834 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4147 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.481 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88M1 1L23 23M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.6819 3.96914 7.65661 6.06 6.06L17.94 17.94Z"
            stroke="#98A2B3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const EyeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" stroke="#98A2B3" strokeWidth="2" />
    </svg>
);

function LoginPage() {
    const { login } = useContext(AuthContext);
    usePageTitle();
    const [searchParams, setSearchParams] = useSearchParams();
    const showForgotPassword = searchParams.get("forgotpassword") === "true";
    const navigate = useNavigate();

    const [loginForm, setLoginForm] = useState({
        userId: "",
        password: "",
        showPassword: false,
        rememberMe: false,
        error: "",
        isLoading: false,
        fieldErrors: {
            userId: false,
            password: false,
        },
    });

    const [forgotPassword, setForgotPassword] = useState({
        step: 1,
        userId: "",
        showOTP: false,
        otp: "",
        isSendingOTP: false,
        isVerifyingOTP: false,
        otpTimer: 0,
        otpError: false,
        newPassword: "",
        confirmPassword: "",
        showNewPassword: false,
        showConfirmPassword: false,
        isResetting: false,
    });

    useEffect(() => {
        const savedUsername = localStorage.getItem(REMEMBER_USERNAME_KEY);
        if (savedUsername) {
            setLoginForm((prev) => ({
                ...prev,
                userId: savedUsername,
                rememberMe: true,
            }));
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = {
            userId: !loginForm.userId.trim(),
            password: !loginForm.password,
        };

        if (errors.userId || errors.password) {
            setLoginForm((prev) => ({
                ...prev,
                error: "Please fill in all fields",
                fieldErrors: errors,
            }));
            return;
        }

        setLoginForm((prev) => ({
            ...prev,
            error: "",
            fieldErrors: { userId: false, password: false },
            isLoading: true,
        }));

        try {
            await login(loginForm.userId.trim(), loginForm.password);
            if (loginForm.rememberMe) {
                localStorage.setItem(REMEMBER_USERNAME_KEY, loginForm.userId.trim());
            } else {
                localStorage.removeItem(REMEMBER_USERNAME_KEY);
            }
            navigate("/");
        } catch (err) {
            setLoginForm((prev) => ({
                ...prev,
                error: err?.response?.data?.detail || "Login Failed",
            }));
        } finally {
            setLoginForm((prev) => ({ ...prev, isLoading: false }));
        }
    };

    const togglePasswordVisibility = () => {
        setLoginForm((prev) => ({ ...prev, showPassword: !prev.showPassword }));
    };

    useEffect(() => {
        if (forgotPassword.otpTimer > 0) {
            const timer = setTimeout(() => {
                setForgotPassword((prev) => ({ ...prev, otpTimer: prev.otpTimer - 1 }));
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [forgotPassword.otpTimer]);

    const handleGetForgotPasswordOTP = async (e) => {
        e.preventDefault();
        setLoginForm((prev) => ({ ...prev, error: "" }));

        if (!forgotPassword.userId.trim()) {
            setLoginForm((prev) => ({ ...prev, error: "Please enter your user ID" }));
            return;
        }

        setForgotPassword((prev) => ({ ...prev, isSendingOTP: true }));
        try {
            await sendForgotPasswordOTP(forgotPassword.userId.trim());
            setForgotPassword((prev) => ({ ...prev, showOTP: true, otpTimer: 240 }));
            setLoginForm((prev) => ({ ...prev, error: "" }));
        } catch (err) {
            setLoginForm((prev) => ({
                ...prev,
                error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to send OTP. Please try again.",
            }));
        } finally {
            setForgotPassword((prev) => ({ ...prev, isSendingOTP: false }));
        }
    };

    const handleVerifyForgotPasswordOTP = async () => {
        if (!forgotPassword.otp || forgotPassword.otp.length !== 6) {
            setLoginForm((prev) => ({ ...prev, error: "Please enter a valid 6-digit OTP" }));
            return;
        }

        setForgotPassword((prev) => ({ ...prev, isVerifyingOTP: true, otpError: false }));
        setLoginForm((prev) => ({ ...prev, error: "" }));
        try {
            await verifyForgotPasswordOTP(forgotPassword.userId.trim(), forgotPassword.otp);
            setForgotPassword((prev) => ({ ...prev, otpError: false, step: 2 }));
            setLoginForm((prev) => ({ ...prev, error: "" }));
        } catch (err) {
            setForgotPassword((prev) => ({ ...prev, otpError: true }));
            setLoginForm((prev) => ({
                ...prev,
                error: err?.response?.data?.detail || "Invalid OTP. Please try again.",
            }));
        } finally {
            setForgotPassword((prev) => ({ ...prev, isVerifyingOTP: false }));
        }
    };

    const handleResendForgotPasswordOTP = async () => {
        if (forgotPassword.otpTimer > 0) return;
        setForgotPassword((prev) => ({ ...prev, otpError: false, otp: "" }));
        await handleGetForgotPasswordOTP({ preventDefault: () => {} });
    };

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    const handleContinueFromOTP = async (e) => {
        e.preventDefault();
        await handleVerifyForgotPasswordOTP();
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoginForm((prev) => ({ ...prev, error: "" }));

        if (!forgotPassword.newPassword || !forgotPassword.confirmPassword) {
            setLoginForm((prev) => ({ ...prev, error: "Please fill in all password fields" }));
            return;
        }

        if (forgotPassword.newPassword.length < 8) {
            setLoginForm((prev) => ({ ...prev, error: "Password must be at least 8 characters" }));
            return;
        }

        if (forgotPassword.newPassword !== forgotPassword.confirmPassword) {
            setLoginForm((prev) => ({ ...prev, error: "Passwords do not match" }));
            return;
        }

        setForgotPassword((prev) => ({ ...prev, isResetting: true }));
        try {
            await resetPassword(
                forgotPassword.userId.trim(),
                forgotPassword.otp,
                forgotPassword.newPassword
            );
            setLoginForm((prev) => ({ ...prev, error: "" }));
            setForgotPassword((prev) => ({ ...prev, step: 3 }));
        } catch (err) {
            setLoginForm((prev) => ({
                ...prev,
                error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to reset password. Please try again.",
            }));
        } finally {
            setForgotPassword((prev) => ({ ...prev, isResetting: false }));
        }
    };

    const handleDone = () => {
        setSearchParams({});
        setForgotPassword({
            step: 1,
            userId: "",
            showOTP: false,
            otp: "",
            isSendingOTP: false,
            isVerifyingOTP: false,
            otpTimer: 0,
            otpError: false,
            newPassword: "",
            confirmPassword: "",
            showNewPassword: false,
            showConfirmPassword: false,
            isResetting: false,
        });
        setLoginForm((prev) => ({ ...prev, error: "" }));
    };

    const forgotHeading =
        forgotPassword.step === 1
            ? "Forgot Password?"
            : forgotPassword.step === 2
              ? "Set your New Password"
              : "Password Reset!";

    const forgotSubtitle =
        forgotPassword.step === 1
            ? "Enter your registered user ID to recover your password"
            : forgotPassword.step === 2
              ? "Your new password should be different from the previously used."
              : "";

    return (
        <div className={styles.container} data-theme="light">
            <div className={styles["left-section-wrapper"]}>
                <div className={styles["left-section"]}>
                    <div className={styles["brand-illustration"]}>
                        <BrandIllustration />
                    </div>
                    <div className={styles["bottom-content"]}>
                        <div className={styles["text-group"]}>
                            <h1 className={styles["promo-heading"]}>One login. Every trade. Every broker.</h1>
                            <p className={styles["promo-text"]}>
                                TradeLyft is your unified execution terminal, bringing orders, positions, margins and settlements into a single, intelligent interface. Eliminate the friction of fragmented workflows and give your desk the clarity it needs to operate at full speed, from the moment markets open.
                            </p>
                        </div>
                        <div className={styles["carousel-indicators"]} aria-hidden="true">
                            <span className={styles["indicator-dot-end"]} />
                            <span className={styles["indicator-active"]} />
                            <span className={styles["indicator-dot-end"]} />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles["right-section"]}>
                <div className={styles["brand-logo"]}>
                    <TradeLyftLogo />
                </div>

                <form
                    className={styles["form-container"]}
                    onSubmit={showForgotPassword ? handleResetPassword : handleSubmit}
                >
                    <div className={styles["form-header"]}>
                        <h2 className={styles["form-heading"]}>
                            {showForgotPassword ? forgotHeading : "Welcome Back"}
                        </h2>
                        <p className={styles["form-subtitle"]}>
                            {showForgotPassword ? forgotSubtitle : "Sign in to access your dashboard"}
                        </p>
                    </div>

                    <div className={styles["form-content"]}>
                        {showForgotPassword ? (
                            <>
                                {loginForm.error !== "" && (
                                    <div className={styles["error-message"]}>{loginForm.error}</div>
                                )}

                                {forgotPassword.step === 1 && (
                                    <>
                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <span className={styles["input-icon"]}>
                                                    <MailIcon />
                                                </span>
                                                <input
                                                    type="text"
                                                    className={`${styles.input} ${styles["username-input"]} ${styles["email-input"]}`}
                                                    placeholder="Enter your user ID"
                                                    value={forgotPassword.userId}
                                                    onChange={(e) =>
                                                        setForgotPassword((prev) => ({ ...prev, userId: e.target.value }))
                                                    }
                                                    disabled={forgotPassword.showOTP}
                                                />
                                                {!forgotPassword.showOTP && forgotPassword.userId.trim() !== "" && (
                                                    <button
                                                        type="button"
                                                        className={styles["get-otp-button"]}
                                                        onClick={handleGetForgotPasswordOTP}
                                                        disabled={forgotPassword.isSendingOTP}
                                                    >
                                                        {forgotPassword.isSendingOTP ? (
                                                            <>
                                                                <BrandLoader variant="icon" size={14} />
                                                                Sending...
                                                            </>
                                                        ) : (
                                                            "Get OTP"
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {forgotPassword.showOTP && (
                                            <>
                                                <OTPInput
                                                    otp={forgotPassword.otp}
                                                    setOtp={(otp) => setForgotPassword((prev) => ({ ...prev, otp }))}
                                                    isVerifying={forgotPassword.isVerifyingOTP}
                                                    isVerified={false}
                                                    timer={forgotPassword.otpTimer}
                                                    formatTimer={formatTimer}
                                                    onResend={handleResendForgotPasswordOTP}
                                                    hasError={forgotPassword.otpError}
                                                    onErrorClear={() =>
                                                        setForgotPassword((prev) => ({ ...prev, otpError: false }))
                                                    }
                                                    showResendInHeader={false}
                                                />
                                                <div className={styles["resend-otp-container"]}>
                                                    <span className={styles["resend-otp-text"]}>Didn&apos;t receive the OTP? </span>
                                                    <button
                                                        type="button"
                                                        className={styles["resend-otp-link"]}
                                                        onClick={handleResendForgotPasswordOTP}
                                                        disabled={forgotPassword.otpTimer > 0}
                                                    >
                                                        Resend OTP
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        <button
                                            type="button"
                                            className={styles["submit-button"]}
                                            onClick={handleContinueFromOTP}
                                            disabled={
                                                !forgotPassword.showOTP ||
                                                forgotPassword.isVerifyingOTP ||
                                                forgotPassword.otp.length !== 6
                                            }
                                        >
                                            {forgotPassword.isVerifyingOTP ? (
                                                <>
                                                    <BrandLoader variant="icon" size={18} />
                                                    Verifying...
                                                </>
                                            ) : (
                                                "Continue"
                                            )}
                                        </button>

                                        <div className={styles.divider}>
                                            <span className={styles["divider-text"]}>or</span>
                                        </div>

                                        <div className={styles["back-to-signin-container"]}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchParams({});
                                                    handleDone();
                                                }}
                                                className={styles["back-to-signin-link"]}
                                            >
                                                &lt; Back to Sign In
                                            </button>
                                        </div>
                                    </>
                                )}

                                {forgotPassword.step === 2 && (
                                    <>
                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <input
                                                    type={forgotPassword.showNewPassword ? "text" : "password"}
                                                    className={styles.input}
                                                    placeholder="Create New Password"
                                                    value={forgotPassword.newPassword}
                                                    onChange={(e) =>
                                                        setForgotPassword((prev) => ({ ...prev, newPassword: e.target.value }))
                                                    }
                                                    disabled={forgotPassword.isResetting}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles["password-toggle"]}
                                                    onClick={() =>
                                                        setForgotPassword((prev) => ({
                                                            ...prev,
                                                            showNewPassword: !prev.showNewPassword,
                                                        }))
                                                    }
                                                    aria-label={forgotPassword.showNewPassword ? "Hide password" : "Show password"}
                                                >
                                                    {forgotPassword.showNewPassword ? <EyeIcon /> : <EyeOffIcon />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <input
                                                    type={forgotPassword.showConfirmPassword ? "text" : "password"}
                                                    className={styles.input}
                                                    placeholder="Confirm New Password"
                                                    value={forgotPassword.confirmPassword}
                                                    onChange={(e) =>
                                                        setForgotPassword((prev) => ({
                                                            ...prev,
                                                            confirmPassword: e.target.value,
                                                        }))
                                                    }
                                                    disabled={forgotPassword.isResetting}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles["password-toggle"]}
                                                    onClick={() =>
                                                        setForgotPassword((prev) => ({
                                                            ...prev,
                                                            showConfirmPassword: !prev.showConfirmPassword,
                                                        }))
                                                    }
                                                    aria-label={
                                                        forgotPassword.showConfirmPassword ? "Hide password" : "Show password"
                                                    }
                                                >
                                                    {forgotPassword.showConfirmPassword ? <EyeIcon /> : <EyeOffIcon />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles["password-requirements"]}>
                                            Use 8+ characters with a mix of letters and numbers
                                        </div>

                                        <button
                                            type="button"
                                            className={styles["submit-button"]}
                                            onClick={handleResetPassword}
                                            disabled={
                                                forgotPassword.isResetting ||
                                                !forgotPassword.newPassword ||
                                                !forgotPassword.confirmPassword ||
                                                forgotPassword.newPassword.length < 8 ||
                                                forgotPassword.newPassword !== forgotPassword.confirmPassword
                                            }
                                        >
                                            {forgotPassword.isResetting ? (
                                                <>
                                                    <BrandLoader variant="icon" size={18} />
                                                    Resetting...
                                                </>
                                            ) : (
                                                "Confirm"
                                            )}
                                        </button>

                                        <div className={styles.divider}>
                                            <span className={styles["divider-text"]}>or</span>
                                        </div>

                                        <div className={styles["back-to-signin-container"]}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchParams({});
                                                    handleDone();
                                                }}
                                                className={styles["back-to-signin-link"]}
                                            >
                                                &lt; Back to Sign In
                                            </button>
                                        </div>
                                    </>
                                )}

                                {forgotPassword.step === 3 && (
                                    <div className={styles["success-container"]}>
                                        <div className={styles["success-icon"]}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                                                <circle cx="32" cy="32" r="32" fill="var(--color-positive)" />
                                                <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <p className={styles["success-message"]}>
                                            Your password has been successfully reset, click below to Sign in.
                                        </p>
                                        <button type="button" className={styles["submit-button"]} onClick={handleDone}>
                                            Done
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {loginForm.error !== "" && (
                                    <div className={styles["error-message"]}>{loginForm.error}</div>
                                )}

                                <div className={styles["input-group"]}>
                                    <div className={styles["input-wrapper"]}>
                                        <span className={styles["input-icon"]}>
                                            <MailIcon />
                                        </span>
                                        <input
                                            type="text"
                                            className={`${styles.input} ${styles["username-input"]} ${loginForm.fieldErrors.userId ? styles["input-error"] : ""}`}
                                            placeholder="Enter Your Username"
                                            value={loginForm.userId}
                                            onChange={(e) => {
                                                setLoginForm((prev) => ({
                                                    ...prev,
                                                    userId: e.target.value,
                                                    fieldErrors: { ...prev.fieldErrors, userId: false },
                                                }));
                                            }}
                                            disabled={loginForm.isLoading}
                                            aria-label="Username"
                                            autoComplete="username"
                                        />
                                    </div>
                                </div>

                                <div className={styles["input-group"]}>
                                    <div className={styles["input-wrapper"]}>
                                        <input
                                            type={loginForm.showPassword ? "text" : "password"}
                                            className={`${styles.input} ${loginForm.fieldErrors.password ? styles["input-error"] : ""}`}
                                            placeholder="Enter Your Password"
                                            value={loginForm.password}
                                            onChange={(e) => {
                                                setLoginForm((prev) => ({
                                                    ...prev,
                                                    password: e.target.value,
                                                    fieldErrors: { ...prev.fieldErrors, password: false },
                                                }));
                                            }}
                                            disabled={loginForm.isLoading}
                                            aria-label="Password"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            className={styles["password-toggle"]}
                                            onClick={togglePasswordVisibility}
                                            disabled={loginForm.isLoading}
                                            aria-label={loginForm.showPassword ? "Hide password" : "Show password"}
                                        >
                                            {loginForm.showPassword ? <EyeIcon /> : <EyeOffIcon />}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles["remember-row"]}>
                                    <input
                                        type="checkbox"
                                        id="remember-me"
                                        checked={loginForm.rememberMe}
                                        onChange={(e) => {
                                            setLoginForm((prev) => ({ ...prev, rememberMe: e.target.checked }));
                                        }}
                                        className={styles["remember-checkbox"]}
                                        disabled={loginForm.isLoading}
                                    />
                                    <label htmlFor="remember-me" className={styles["remember-label"]}>
                                        Remember me
                                    </label>
                                </div>

                                <div className={styles["forget-password-container"]}>
                                    <button
                                        type="button"
                                        onClick={() => setSearchParams({ forgotpassword: "true" })}
                                        className={styles["primary-link"]}
                                    >
                                        Forget Password?
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className={styles["submit-button"]}
                                    disabled={loginForm.isLoading}
                                    aria-label="Sign in"
                                >
                                    {loginForm.isLoading ? (
                                        <>
                                            <BrandLoader variant="icon" size={18} />
                                            Signing in...
                                        </>
                                    ) : (
                                        "Sign in"
                                    )}
                                </button>

                                <div className={styles.divider}>
                                    <span className={styles["divider-text"]}>or</span>
                                </div>

                                <div className={styles["form-footer"]}>
                                    <p className={styles["terms-text"]}>
                                        By Sign in an account, you agreeing to our{" "}
                                        <Link to="/privacy-policy" className={styles["primary-link"]}>
                                            Privacy Policy
                                        </Link>
                                        , and{" "}
                                        <Link to="/terms-of-service" className={styles["primary-link"]}>
                                            Term & Conditions
                                        </Link>
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default LoginPage;
